// @ts-nocheck

// App
import { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';

// Components
import AdvertisementWrapper from 'app/shared/modules/AdvertisementWrapper';
import BreadcrumbNav from 'app/shared/modules/BreadcrumbNav';
import ListingHeader from 'app/shared/modules/ListingHeader';
import SrpContentWrapper from 'app/shared/modules/MobileSRP/__BaseComponents__/SrpContentWrapper';
import Pagination from 'app/shared/modules/Pagination/Pagination';
import FilterPills from 'app/shared/modules/MobileSRP/ListingCards/FilterPills';
import NearbyAreaListings from 'app/shared/modules/NearbyAreaListings';
import NoListingsFound from 'app/shared/modules/NoListingsFound/NoListingsFound';
import ListSkeleton from 'app/shared/modules/MobileSRP/ListingCards/ListSkeleton';
import InfoCard from 'app/shared/modules/MobileSRP/ListingCards/InfoCard';

// Hooks
import { useCommuteTimes, usePrevious } from './hooks';

// Misc / utils
import VALUE_CONSTANTS from 'app/shared/constants/ValueConstants';
import { DEFAULT } from 'app/shared/models/Filter';
import ListingWrapper from 'app/shared/modules/listing-cards/ListingWrapper';
import {
  emitTrackClicksIntoPropertyEvent,
  emitTrackImpressionForPropertyCardEvent,
} from 'app/shared/models/Clickstream/ListingCardClickstreamEvents';

const List = ({ items = [], lazyLoadRef = undefined, handleInitializeMapClick = undefined }) => {
  // Redux
  const searchSlug = useSelector((state) => state.filter.search.slug);
  const slugTitle = useSelector((state) => state.filter.search.title);
  const areaName = useSelector((state) => state.area.area.name);
  const totalListings = useSelector((state) => state.listings.totalListings);

  const currentPage = Number(useSelector((state) => state.location.current.query.page)) || 1;
  const totalBuildings = useSelector((state) => state.listings.totalBuildings);
  const areListingsLoading = useSelector((state) => !state.app.fetchListingsByCoordsComplete);
  const userPoint = useSelector(
    (state) => state.user.userPoints.destinations[state.user.userPoints.activeUserPoint] || null,
  );
  const commuteFilter = useSelector((state) => state.filter.commute);
  const priceFilter = useSelector((state) => state.filter.price);
  const bedFilter = useSelector((state) => state.filter.bedrooms);
  const screenWidth = useSelector((state) => state.app.device.screenWidth);

  const isDesktop = screenWidth !== 'sm';
  // React
  const [shouldHideNativeAd, setShouldHideNativead] = useState({
    slot1: false,
    slot2: false,
  });

  // Hooks
  const [getCommuteTimes, commuteTimes, resetCommuteTimes] = useCommuteTimes();
  const prevListingsLoading = usePrevious(areListingsLoading);
  const hasItems = Boolean(items.length);

  const { commuteTimeMode, commuteMode, commuteLats, commuteLons, commuteTime } = commuteFilter;
  const isCommuteFilterSet = Boolean(
    commuteTimeMode && commuteMode && commuteLats && commuteLons && commuteTime !== null,
  );

  /**
   * Fetch commute times to be displayed on the listing cards if:
   * - Commute filter has been set
   */
  useEffect(() => {
    const shouldGetCommuteTimes = hasItems && userPoint && isCommuteFilterSet && commuteTimes.length === 0;

    if (shouldGetCommuteTimes) {
      getCommuteTimes({ items, commuteFilter, userPoint });
    }
  }, [commuteFilter, commuteTimes.length, getCommuteTimes, hasItems, isCommuteFilterSet, items, userPoint]);

  /**
   * Fetch new commute times to be displayed on the listing cards if:
   * - There is a listing change AND
   * - Commute filter has been set
   */
  useEffect(() => {
    const isDoneLoadingListings = prevListingsLoading === true && areListingsLoading === false;
    if (isEqual(commuteFilter, DEFAULT.commute)) {
      resetCommuteTimes();
    } else if (isCommuteFilterSet && isDoneLoadingListings) {
      getCommuteTimes({ items, commuteFilter, userPoint });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areListingsLoading, commuteFilter]);

  const aboveTheFoldItemsSlice = items.slice(0, 2);
  const belowTheFoldItemsSlice = items.slice(2, VALUE_CONSTANTS.MAX_LISTINGS_PER_PAGE);

  const maxPage = Math.max(Math.ceil(totalBuildings / VALUE_CONSTANTS.MAX_LISTINGS_PER_PAGE), 1);
  const showListingAd = items.length > 2;

  const hideAdElement = (slot, hideNativeAd) => {
    if (shouldHideNativeAd[slot] !== hideNativeAd) {
      setShouldHideNativead({
        ...shouldHideNativeAd,
        [slot]: hideNativeAd,
      });
    }
  };

  const trackImpression = (listing) => {
    emitTrackImpressionForPropertyCardEvent({ listing, triggerLocation: 'search_results_list' });
  };

  const trackClick = (listing) => {
    emitTrackClicksIntoPropertyEvent({ listing, triggerLocation: 'search_results_list' });
  };

  const wrapAboveTheFoldListings = () => {
    const renderedListings = aboveTheFoldItemsSlice.map((item, idx) => {
      return (
        <ListingWrapper
          listing={item}
          key={item.aliasEncoded}
          shouldLazyLoadListingCardPhotos={false}
          highPriority={idx === 0}
          onClick={() => trackClick(item)}
          trackImpression={trackImpression}
          setActiveMarkerOnHover={false}
          triggerLocation="search_results"
          triggerObject="property_card"
        />
      );
    });

    return renderedListings;
  };

  const wrapBelowTheFoldListings = () => {
    const hasActivePriceFilter = priceFilter.max !== DEFAULT.price.max && priceFilter.min !== DEFAULT.price.min;
    const hasActiveBedFilter = !isEqual(bedFilter, DEFAULT.bedrooms);
    const bothInfoCardFiltersActive = hasActivePriceFilter && hasActiveBedFilter;
    const renderedListings = belowTheFoldItemsSlice.map((item, idx) => {
      const spliceInfoCard =
        ((currentPage === 1 && maxPage > 1) || areListingsLoading) && !bothInfoCardFiltersActive && idx === 4;
      const shouldLazyLoad = true;

      if (spliceInfoCard) {
        return (
          <Fragment key={`${item.aliasEncoded}-with-info-card`}>
            <InfoCard key="ListingInfoCard" />
            <ListingWrapper
              listing={item}
              key={item.aliasEncoded}
              shouldLazyLoadListingCardPhotos={shouldLazyLoad}
              onClick={() => trackClick(item)}
              trackImpression={trackImpression}
              setActiveMarkerOnHover={false}
              triggerLocation="search_results"
              triggerObject="property_card"
              deferRender={shouldLazyLoad}
            />
          </Fragment>
        );
      } else {
        return (
          <ListingWrapper
            listing={item}
            key={item.aliasEncoded}
            shouldLazyLoadListingCardPhotos={shouldLazyLoad}
            onClick={() => trackClick(item)}
            trackImpression={trackImpression}
            setActiveMarkerOnHover={false}
            triggerLocation="search_results"
            triggerObject="property_card"
            deferRender={shouldLazyLoad}
          />
        );
      }
    });

    return renderedListings;
  };

  const wrapAdsAroundListings = (atfListings, btfListings) => {
    const combinedListings = atfListings.concat(btfListings);
    // Only wrap the listings with the AdWrapper if showListingAd is true and user is on page 1
    if (showListingAd && currentPage === 1) {
      return (
        <AdvertisementWrapper
          handleHideAdElement={hideAdElement}
          hideSlot1={shouldHideNativeAd.slot1}
          hideSlot2={shouldHideNativeAd.slot2}
        >
          {combinedListings}
        </AdvertisementWrapper>
      );
    } else {
      return combinedListings;
    }
  };

  const aboveTheFoldRenderedListingsArray = wrapAboveTheFoldListings(); // Each <li> will contain ListingWrapper
  const belowTheFoldDeferredRenderedListingsArray = wrapBelowTheFoldListings(); // Each <li> will contain a DeferredRender element which will contain ListingWrapper
  const adWrappedListingsArray = wrapAdsAroundListings(
    aboveTheFoldRenderedListingsArray,
    belowTheFoldDeferredRenderedListingsArray,
  );

  return (
    <SrpContentWrapper
      identifier="listings"
      customHeader={
        <ListingHeader
          searchSlug={searchSlug}
          slugTitle={slugTitle}
          totalListings={totalListings}
          currentPage={currentPage}
          totalBuildings={totalBuildings}
          isDesktop={isDesktop}
        />
      }
    >
      {areListingsLoading ? (
        <ListSkeleton count={20} />
      ) : (
        <>
          <ul>{adWrappedListingsArray}</ul>
          {currentPage === maxPage && !areListingsLoading && items.length !== 0 && (
            <>
              <FilterPills />
              <NearbyAreaListings currentAreaName={areaName} />
            </>
          )}
        </>
      )}
      {!areListingsLoading && !hasItems && <NoListingsFound handleInitializeMapClick={handleInitializeMapClick} />}
      {hasItems && maxPage !== 1 && <Pagination />}
      <footer ref={lazyLoadRef}>
        <BreadcrumbNav />
      </footer>
    </SrpContentWrapper>
  );
};

export default List;
